import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, Typography} from '@mui/material'
import moment from 'moment'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import AppDialog from 'src/components-new/feedback/AppDialog'
import FilePreviewDialog from 'src/components-new/feedback/FilePreviewDialog'
import ActivityDetailDialogNew from 'src/components-new/feedback/ActivityDetailDialogNew'
import {ACTIVITY_STATES} from 'src/components-new/feedback/activityDetailUtils'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import Button from 'src/components-new/primitives/Button'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import PatientEventDialogNew from 'src/pages-new/patient/tabs/activities/PatientEventDialogNew'
import VirtualMeetingDetailDialogNew from 'src/pages-new/patient/tabs/activities/VirtualMeetingDetailDialogNew'

interface ActivitiesTabProps {
  patientId: string
}

interface ActivityRow {
  _id: string
  plannedDate?: string
  title?: string
  type?: string
  typeLabel?: string
  description?: string
  [key: string]: any
}

interface ActivityColumnConfig {
  id: string
  label: string
}

interface ActivityFilterConfig {
  id: string
  label?: string
}

/** Mirrors legacy `userEventsTypes` — used both for the "Tipo" filter options and to
 * derive the human-readable `typeLabel` shown in the "Tipo" column. */
const USER_EVENT_TYPE_OPTIONS = [
  {value: 'USER_ACTIVITY', label: 'Attività'},
  {value: 'PERSONAL_EVENT', label: 'Evento Paziente'},
  {value: 'VIRTUAL_MEETING', label: 'Incontro virtuale'},
]

// The only activity states psp-backend returns in this list (and accepts as `state` filter).
const FILTERABLE_ACTIVITY_STATES = ['TO_BE_CONCLUDED', 'TO_BE_VERIFIED', 'COMPLETED', 'CANCELLED']

const FILTER_ITEMS: Record<string, FilterItem> = {
  date: {id: 'date', label: 'Data', type: 'rangePicker'},
  type: {id: 'type', label: 'Tipo', type: 'select', options: USER_EVENT_TYPE_OPTIONS},
  state: {
    id: 'state',
    label: 'Stato',
    type: 'select',
    options: ACTIVITY_STATES
      .filter(({value}) => FILTERABLE_ACTIVITY_STATES.includes(value))
      .map(({value, label}) => ({value, label})),
  },
}

// Filters shown before activitiesConfiguration.filtersConfig is saved from the PSP configurator
// ("Dettaglio paziente > Attività"). Keep the ids in sync with patientActivitiesFilterOptions in
// Internal Portals/ia-web-components/src/react-components/PspConfiguratorModal/CONSTANTS.ts.
const DEFAULT_FILTERS: ActivityFilterConfig[] = [{id: 'date'}, {id: 'type'}]

function buildFilterConfig(filtersConfig?: ActivityFilterConfig[]): FilterItem[] {
  return (filtersConfig ?? DEFAULT_FILTERS).reduce<FilterItem[]>((items, {id, label}) => {
    const item = FILTER_ITEMS[id]
    if (item) items.push({...item, label: label || item.label})
    return items
  }, [])
}

/** Mirrors legacy DEFAULT_COLUMNS: for tutor/quiperte scope the 3rd column shows the raw
 * `description` field, otherwise it shows the mapped `type` label. Row-click routing below
 * relies on the untouched `activity.type` enum value, so (unlike legacy, which overwrites
 * `type` in place with its label) the mapped label is kept in a separate `typeLabel` field. */
function getDefaultColumns(scope: string): ActivityColumnConfig[] {
  const isTutorScope = ['tutor', 'quiperte'].includes(scope)
  return [
    {id: 'plannedDate', label: isTutorScope ? 'Data' : 'Data inizio'},
    {id: 'title', label: isTutorScope ? 'Descrizione' : 'Titolo'},
    {id: isTutorScope ? 'description' : 'type', label: isTutorScope ? 'Tipologia' : 'Tipo'},
  ]
}

function renderCell(id: string) {
  return (row: ActivityRow): React.ReactNode => {
    if (id === 'plannedDate') {
      return <Typography variant="body2">{row.plannedDate ? moment(row.plannedDate).format('DD MMMM YYYY HH:mm') : '—'}</Typography>
    }
    if (id === 'type') {
      return <Typography variant="body2">{row.typeLabel ?? '—'}</Typography>
    }
    // Only activity instances have a state: appointments show the placeholder.
    if (id === 'state') {
      const activityState = ACTIVITY_STATES.find(state => state.value === row.state)
      return activityState
        ? <StatusPill label={activityState.label} color={activityState.color} />
        : <Typography variant="body2">{'—'}</Typography>
    }
    // Bold, like the patients list's own "Paziente" header column — this is the
    // column used as the mobile card's title (mobileCardHeader below).
    if (id === 'title') {
      return <Typography variant="body2" fontWeight={600}>{row.title ?? '—'}</Typography>
    }
    return <Typography variant="body2">{row[id] ?? '—'}</Typography>
  }
}

// user-events mixes appointments (PERSONAL_EVENT / VIRTUAL_MEETING) with activity instances:
// only the latter carry `beneficiaryId` and have an exportable activity sheet. `type` can't be
// used here, since activity instances may carry a free-text one.
function isActivityInstance(row: ActivityRow): boolean {
  return Boolean(row.beneficiaryId)
}

// The activity sheet is shown only once the activity is completed.
function hasActivitySheet(row: ActivityRow): boolean {
  return isActivityInstance(row) && row.state === 'COMPLETED'
}

// Id of the "Scheda attività" column (view/download buttons) in activitiesConfiguration.columnOptions:
// keep it in sync with patientActivitiesColumnOptions in
// Internal Portals/ia-web-components/src/react-components/PspConfiguratorModal/CONSTANTS.ts.
const ACTIVITY_SHEET_COLUMN_ID = 'activitySheet'

const INITIAL_VIEWER_STATE = {open: false, filePath: '', mime: '', fileName: ''}

function buildActivitySheetName(row: ActivityRow, patientCode?: string): string {
  const dateStr = row.plannedDate ? moment(row.plannedDate).format('DD-MM-YYYY') : ''
  return `Scheda_attivita_${patientCode ?? ''}_${dateStr}.pdf`
}

function forceDownload(blob: Blob, downloadName: string): void {
  const url = (window.URL || window.webkitURL).createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = downloadName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

export default function ActivitiesTab({patientId}: ActivitiesTabProps): JSX.Element {
  const toast = useToast()
  const scope = useAppSelector(state => state.pspConfigReducer.data.scope)
  const activitiesConfiguration = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.activitiesConfiguration
  )

  const [rows, setRows] = useState<ActivityRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: '-createdAt'})
  const [filters, setFilters] = useState<FilterResult[]>([])

  const [eventsTypes, setEventsTypes] = useState<Record<string, any>[]>([])
  const [currentPatient, setCurrentPatient] = useState<Record<string, any>>()

  const [selectedActivity, setSelectedActivity] = useState<ActivityRow>()
  const [openPatientEventDialog, setOpenPatientEventDialog] = useState(false)
  const [openVirtualMeetingDialog, setOpenVirtualMeetingDialog] = useState(false)
  const [openActivityDetailDialog, setOpenActivityDetailDialog] = useState(false)
  const [openTestDBSDialog, setOpenTestDBSDialog] = useState(false)

  const [statusDownload, setStatusDownload] = useState<string>()
  const [viewerState, setViewerState] = useState(INITIAL_VIEWER_STATE)

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getPatientsUserEvents(patientId, filters, setStatus, pagination),
      apiClient.getPatientsUserEventsCount(patientId, filters, setStatusCount),
    ])
      .then(([events, countResult]) => {
        if (Array.isArray(events)) {
          setRows(events.map((userEvent: any) => ({
            ...userEvent,
            typeLabel: USER_EVENT_TYPE_OPTIONS.find(type => type.value === userEvent.type)?.label
              ?? userEvent.description
              ?? 'Attività',
          })))
        }
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [patientId, filters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Supplementary catalogs needed by PatientEventDialogNew — fetched once per patient,
  // kept off the main `status`/`statusCount` gating (they don't drive the table's loading
  // state or error banner, only the create/edit dialog).
  useEffect(() => {
    let cancelled = false
    apiClient.getUserEventsTypes(() => {}).then((types: any) => { if (!cancelled) setEventsTypes(types ?? []) })
    apiClient.getPatientsUserData(patientId, () => {}).then((patient: any) => { if (!cancelled) setCurrentPatient(patient) })
    return () => { cancelled = true }
  }, [patientId])

  // Belt-and-suspenders: `pagination.order` is sent to the backend via getPatientsUserEvents,
  // but also re-sort the already-fetched page client-side so the "Data"/"Data inizio" arrow
  // always visibly reorders rows even if the API/mock ignores the `sort` param. Default order
  // is '-createdAt' (unsorted by plannedDate), so this only kicks in once the user clicks it.
  const sortedRows = useMemo(() => {
    const order = pagination.order
    if (!order || (order !== 'plannedDate' && order !== '-plannedDate')) return rows
    const desc = order.startsWith('-')
    return [...rows].sort((a, b) => {
      const aTime = a.plannedDate ? new Date(a.plannedDate).getTime() : 0
      const bTime = b.plannedDate ? new Date(b.plannedDate).getTime() : 0
      return desc ? bTime - aTime : aTime - bTime
    })
  }, [rows, pagination.order])

  const fetchActivitySheet = async (row: ActivityRow): Promise<Blob | undefined> => {
    const response = await apiClient.exportPatientActivity(patientId, row._id, setStatusDownload)
    if (!response) return undefined
    return new Blob([response.data], {type: 'application/pdf'})
  }

  const handlePreviewActivitySheet = async (row: ActivityRow) => {
    const blob = await fetchActivitySheet(row)
    if (!blob) return
    const url = (window.URL || window.webkitURL).createObjectURL(blob)
    setViewerState({open: true, filePath: url, mime: 'application/pdf', fileName: buildActivitySheetName(row, currentPatient?.code)})
  }

  const handleDownloadActivitySheet = async (row: ActivityRow) => {
    const blob = await fetchActivitySheet(row)
    if (!blob) return
    forceDownload(blob, buildActivitySheetName(row, currentPatient?.code))
  }

  const handleCloseViewer = () => {
    URL.revokeObjectURL(viewerState.filePath)
    setViewerState(INITIAL_VIEWER_STATE)
  }

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleActivityClick = (activity: ActivityRow) => {
    setSelectedActivity(activity)
    switch (activity.type) {
      case 'PERSONAL_EVENT':
        setOpenPatientEventDialog(true)
        return
      case 'VIRTUAL_MEETING':
        setOpenVirtualMeetingDialog(true)
        return
      default:
        setOpenActivityDetailDialog(true)
    }
  }

  const handleNewPatientEvent = () => {
    setSelectedActivity(undefined)
    setOpenPatientEventDialog(true)
  }

  const handleClosePatientEventDialog = () => {
    setOpenPatientEventDialog(false)
    setSelectedActivity(undefined)
  }

  // Legacy always creates via postAppointment here, even when editing an existing
  // PERSONAL_EVENT row (there's an unused patchAppointment in apiClient, but
  // Activities.tsx never calls it) — replicated as-is, not "fixed".
  const handleSaveEvent = async (values: Record<string, any>) => {
    // PatientEventDialogNew sets `user` to the full patient object; the backend expects
    // just the ObjectId string (mirrors legacy Activities.tsx normalization).
    const {user, ...rest} = values
    let saveStatus: string | undefined
    await apiClient.postAppointment({...rest, user: user?._id ?? user}, (nextStatus: string) => {
      saveStatus = nextStatus
      setStatus(nextStatus)
    })
    if (saveStatus === STATUS.REJECTED) {
      toast.error('Errore durante il salvataggio dell\'evento')
      return
    }
    toast.success('Evento salvato con successo')
    fetchData()
    handleClosePatientEventDialog()
  }

  const filterConfig = useMemo(
    () => buildFilterConfig(activitiesConfiguration?.filtersConfig),
    [activitiesConfiguration?.filtersConfig]
  )

  const configuredColumns: ActivityColumnConfig[] | undefined = activitiesConfiguration?.columnOptions?.length > 0
    ? activitiesConfiguration.columnOptions
    : undefined
  const columnConfig: ActivityColumnConfig[] = configuredColumns
    ?.filter(col => col.id !== ACTIVITY_SHEET_COLUMN_ID)
    ?? getDefaultColumns(scope)
  // Without configured columns the buttons are shown, as before the column was configurable.
  const activitySheetColumn: ActivityColumnConfig | undefined = configuredColumns
    ? configuredColumns.find(col => col.id === ACTIVITY_SHEET_COLUMN_ID)
    : {id: ACTIVITY_SHEET_COLUMN_ID, label: ''}

  // Mobile cards follow the patient list style: the title is the card's bold header
  // (mobileCardHeader); the "Tipo"/"Tipologia" column is plain text like every other
  // field, not a colored badge — it's a category label, not a status.
  const columns: ColumnDef<ActivityRow>[] = [
    ...columnConfig.map(col => ({
      key: col.id,
      header: col.label,
      mobileCardHeader: col.id === 'title',
      sortKey: col.id === 'plannedDate' ? 'plannedDate' : undefined,
      render: renderCell(col.id),
    })),
    ...(activitySheetColumn ? [{
      key: 'actions',
      header: activitySheetColumn.label,
      width: activitySheetColumn.label ? undefined : 56,
      isActionColumn: true,
      render: (row: ActivityRow) => hasActivitySheet(row) ? (
        <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{display: 'flex', gap: 0.5}}>
          <ActionIconButton variant="view" tooltip="Visualizza scheda attività" aria-label="Visualizza scheda attività" onClick={() => handlePreviewActivitySheet(row)} />
          <ActionIconButton variant="download" tooltip="Scarica scheda attività" aria-label="Scarica scheda attività" onClick={() => handleDownloadActivitySheet(row)} />
        </Box>
      ) : null,
    }] : []),
  ]

  return (
    <Box>
      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel caricare i dati.'}
        </InlineMessage>
      )}

      {statusDownload === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatusDownload(undefined)} sx={{mb: 2}}>
          {'Errore durante il download della scheda attività.'}
        </InlineMessage>
      )}

      <Box sx={{display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, mb: 2}}>
        {activitiesConfiguration?.testDBS && (
          <Button variant="primary" onClick={() => setOpenTestDBSDialog(true)}>Richiedi test DBS</Button>
        )}
        {activitiesConfiguration?.patientEvent && (
          <Button variant="primary" onClick={handleNewPatientEvent}>Nuovo evento paziente</Button>
        )}
        {filterConfig.length > 0 && <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />}
      </Box>

      <DataTable<ActivityRow>
        columns={columns}
        rows={sortedRows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessun dato trovato"
        onRowClick={handleActivityClick}
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
        sortBy={pagination.order}
        onSortChange={(order) => setPagination(prev => ({...prev, order, page: 0}))}
        mobileActionsPosition="bottom"
      />

      <FilePreviewDialog
        fileName={viewerState.fileName}
        filePath={viewerState.filePath}
        mime={viewerState.mime}
        onClose={handleCloseViewer}
        open={viewerState.open}
        status={STATUS.FULFILLED}
      />

      <PatientEventDialogNew
        open={openPatientEventDialog}
        event={selectedActivity}
        eventsTypes={eventsTypes}
        patientDetailUser={currentPatient}
        onClose={handleClosePatientEventDialog}
        onSave={handleSaveEvent}
      />

      <VirtualMeetingDetailDialogNew
        open={openVirtualMeetingDialog}
        event={selectedActivity}
        onClose={() => setOpenVirtualMeetingDialog(false)}
      />

      {selectedActivity && openActivityDetailDialog && (
        <ActivityDetailDialogNew
          activity={selectedActivity}
          open={openActivityDetailDialog}
          onClose={() => setOpenActivityDetailDialog(false)}
          showDocuments
        />
      )}

      {/* Legacy has no API call wired to either button here — both just close the
          dialog. Dead/no-op in production today; replicated as-is. */}
      <AppDialog
        open={openTestDBSDialog}
        onClose={() => setOpenTestDBSDialog(false)}
        title="Richiedi Test DBS"
        maxWidth="xs"
        actions={
          <>
            <Button variant="secondary" onClick={() => setOpenTestDBSDialog(false)}>Annulla</Button>
            <Button variant="primary" onClick={() => setOpenTestDBSDialog(false)}>Ok</Button>
          </>
        }
      >
        <Typography variant="body2">Confermi la richiesta di un Test DBS per il paziente?</Typography>
      </AppDialog>
    </Box>
  )
}
